import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/client';
import { FiTag, FiX } from 'react-icons/fi';

const FloatingCoupon = () => {
  const [coupons, setCoupons] = useState([]);
  const [currentCouponIndex, setCurrentCouponIndex] = useState(0);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const fetchActiveCoupons = async () => {
      try {
        // Query for active coupons that haven't expired
        const couponsQuery = query(
          collection(db, 'coupons'), 
          where('isActive', '==', true)
        );
        
        const couponsSnapshot = await getDocs(couponsQuery);
        const couponsList = couponsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(coupon => {
            // Filter out expired coupons
            if (!coupon.expiryDate) return true;
            
            // Check if the coupon has an expiry date and it's in the future
            if (coupon.expiryDate.seconds) {
              return new Date(coupon.expiryDate.seconds * 1000) > new Date();
            }
            return true;
          });
        
        setCoupons(couponsList);
      } catch (error) {
        console.error('Error fetching coupons:', error);
      }
    };

    fetchActiveCoupons();
  }, []);

  useEffect(() => {
    // Only set up the interval if there are multiple coupons
    if (coupons.length > 1) {
      const interval = setInterval(() => {
        setCurrentCouponIndex((prevIndex) => (prevIndex + 1) % coupons.length);
      }, 5000); // Change coupon every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [coupons.length]);

  if (!showBanner || coupons.length === 0) {
    return null;
  }

  const currentCoupon = coupons[currentCouponIndex];

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed bottom-4 left-0 right-0 mx-auto w-full max-w-md z-50"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <div 
          className="mx-4 p-4 rounded-lg shadow-lg flex items-center justify-between"
          style={{ 
            background: 'linear-gradient(135deg, #ff6b6b 0%, #a16bff 100%)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <div className="flex items-center">
            <div className="bg-white p-2 rounded-full mr-3">
              <FiTag className="text-xl" style={{ color: 'var(--primary)' }} />
            </div>
            <div>
              <h3 className="font-bold text-white">{currentCoupon.code}</h3>
              <p className="text-sm text-white opacity-90">
                {currentCoupon.type === 'percentage' 
                  ? `${currentCoupon.value}% off${currentCoupon.minAmount > 0 ? ` on orders above ₹${currentCoupon.minAmount}` : ''}`
                  : `₹${currentCoupon.value} off${currentCoupon.minAmount > 0 ? ` on orders above ₹${currentCoupon.minAmount}` : ''}`
                }
                {currentCoupon.description && ` - ${currentCoupon.description}`}
              </p>
            </div>
          </div>
          <button 
            onClick={() => setShowBanner(false)}
            className="text-white p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <FiX />
          </button>
        </div>
        
        {coupons.length > 1 && (
          <div className="flex justify-center mt-2">
            {coupons.map((_, idx) => (
              <motion.div
                key={idx}
                className="w-2 h-2 mx-1 rounded-full bg-white bg-opacity-70"
                animate={{
                  scale: currentCouponIndex === idx ? 1.5 : 1,
                  opacity: currentCouponIndex === idx ? 1 : 0.5,
                }}
                transition={{ duration: 0.3 }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingCoupon; 